/**
 * Shared map base layers (design system README: "Map: satellite tiles
 * with GPS trace overlay"). Renders a Leaflet layers toggle with
 * Satellite as the default and Street one click away — the circuit
 * editor leans on OSM road labels when tracing a route, so we offer
 * both rather than hard-swapping.
 *
 * Esri World Imagery is the keyless satellite source (no token, no
 * per-tile billing to manage). Note the {z}/{y}/{x} order — Esri's
 * REST tile scheme, not OSM's {z}/{x}/{y}. `maxNativeZoom` caps real
 * tiles at 19 and lets Leaflet upscale past that, so deep-zoom drawing
 * in the circuit editor never hits blank tiles.
 *
 * Typed loosely on `RL` because the map components carry their own
 * (duplicated) MapModules shapes — all that matters is the dynamically
 * loaded react-leaflet module.
 */

import React from 'react';

const SATELLITE_URL =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const SATELLITE_ATTR =
  'Tiles &copy; Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community';
const STREET_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const STREET_ATTR = '&copy; OpenStreetMap';

export const BaseLayers: React.FC<{
  /** The dynamically loaded react-leaflet module (modules.RL). */
  modules: { RL: any };
  /** Which base layer starts checked (default satellite). */
  defaultLayer?: 'satellite' | 'street';
  /** Leaflet control corner — defaults to top-left, beside the zoom buttons. */
  position?: 'topleft' | 'topright' | 'bottomleft' | 'bottomright';
}> = ({ modules, defaultLayer = 'satellite', position = 'topleft' }) => {
  const { LayersControl, TileLayer } = modules.RL;
  const { BaseLayer } = LayersControl;
  return (
    <LayersControl position={position}>
      <BaseLayer checked={defaultLayer === 'satellite'} name="Satellite">
        <TileLayer
          attribution={SATELLITE_ATTR}
          url={SATELLITE_URL}
          maxNativeZoom={19}
          maxZoom={22}
        />
      </BaseLayer>
      <BaseLayer checked={defaultLayer === 'street'} name="Street">
        <TileLayer attribution={STREET_ATTR} url={STREET_URL} maxZoom={19} />
      </BaseLayer>
    </LayersControl>
  );
};

export default BaseLayers;
